/**
 * O cartão de folha de um favorecido de conta apartada, na aba Outros Bancos.
 *
 * Papel: reproduz o cartão de `renderOutrosBancos` (l. 3618–3686): o cabeçalho
 * "Folha de Pagamento <iniciais>" com "+ Novo colaborador"; o nome; o salário
 * líquido do mês; a ajuda de custo do cadastro com os dias do mês e o 🧾; a
 * guia DAE; e o rodapé com "Gerar Ordem de Pagamento", "Fechar Folha" e o
 * total.
 *
 * Como usar: quem monta passa o favorecido, a situação do mês que a API
 * devolveu, a folha fechada do mês desse favorecido e as permissões; o cartão
 * grava o salário, os dias, a remoção da guia e o fechamento por conta própria
 * e avisa pelo `useNotificacao`. "Importar (PDF)" e "Importar Guia
 * DAE"/"Reimportar" só chamam `onImportarSalario` e `onImportarGuia`: a leitura
 * do PDF e a conferência são de quem monta. Quem só tem `ver` vê os mesmos campos em
 * `readOnly`, o 🧾 e "Gerar Ordem de Pagamento", que só leem, e nenhum botão
 * que grava.
 *
 * O salário grava ao confirmar (sair do campo com o valor mudado), com
 * `importado: false`, e **não apaga** a marca de importação: o "✓" continua
 * depois de uma correção à mão. Os dias gravam os dois tipos como estão à
 * vista, para o tipo que ninguém tocou não saltar para 30; o vazio vale 0. A
 * linha de um tipo com diária continua no cartão com 0 dia, valendo
 * "R$ 0,00", para o 0 poder ser desfeito.
 *
 * "Fechar Folha" confere na tela o que a rota também recusa: o cartão sem item
 * nenhum não chega à API. Antes, se a folha já fechada tinha salário ou guia e
 * o cartão perdeu um deles, a pessoa confirma que aceita tirá-los do
 * Fechamento. A fotografia é montada pela API a partir do banco, e não a partir
 * do que está na tela.
 *
 * "Fechar Folha", "Gerar Ordem de Pagamento" e o 🧾 decidem pela `situacao`, e
 * ficam desabilitados enquanto `useCartaoGravando` diz que alguma gravação do
 * cartão não voltou — a do próprio cartão ou a das caixas de conferência. O
 * salário e os dias gravam quando o campo perde o foco, que é o `mousedown` do
 * próprio botão: habilitado, o clique decidiria com a situação de antes, e o
 * fechamento correria com a gravação e sairia sem o valor novo.
 */
import {
  type Favorecido,
  type FolhaFechada,
  type SituacaoDoMes,
  competenciaComBarra,
  itensDaOrdemDePagamento,
  itensDeOutrosBancos,
  montarFolhaFechada,
  tituloDaFolha,
  totalDoCartao,
} from '@concierge/shared';
import { IconReceipt } from '@tabler/icons-react';

import { useNotificacao } from '@/shared/embutido';
import { formatarData } from '@/shared/lib/formato';
import { lerValorDigitado, mascararCentavos } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import {
  abrirEmAbaNova,
  caminhoDaOrdemDePagamento,
  caminhoDoReciboDeOutrosBancos,
} from '../documentos';
import { formatarReais } from '../formato';
import {
  useCartaoGravando,
  useFecharFolha,
  useGravarDias,
  useGravarSalario,
  useRemoverGuiaDeOutrosBancos,
} from '../hooks/usar-financas-extras';
import { useAoConfirmar } from '../hooks/use-ao-confirmar';
import { dicaDoSalario, diasDoCampo, faltaAoRefechar } from '../outros-bancos';
import { Botao } from './kit/botao';
import { CLASSE_DO_CAMPO_SOLTO } from './kit/campo';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';

/** O rótulo de seção do cartão: 10 px, caixa alta, `.04em`. */
const CLASSE_DO_ROTULO = 'text-[10px] tracking-[0.04em] text-texto-fraco uppercase';

interface CartaoDeFolhaProps {
  favorecido: Favorecido;
  situacao: SituacaoDoMes;
  /** A folha fechada do mês desse favorecido, quando há. */
  fechada: FolhaFechada | undefined;
  /** O mês aberto, em `AAAA-MM`. */
  competencia: string;
  podeEditar: boolean;
  /** Abre e fecha o painel "+ Novo colaborador". */
  onNovoColaborador: () => void;
  /** O "Importar (PDF)" do salário. */
  onImportarSalario: () => void;
  /** O "Importar Guia DAE" ou "Reimportar". */
  onImportarGuia: () => void;
}

/** O botão de importar das linhas de salário e de guia: `padding:2px 8px` e 11 px. */
const CLASSE_DO_IMPORTAR = 'px-2 py-0.5 text-[11px]';

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * O nome da linha de um tipo com diária.
 *
 * @param tipo - `custo` ou `diarias`.
 * @returns "Ajuda de Custo" ou "Diárias", como a origem escreve a linha.
 */
function rotuloDosDias(tipo: string): string {
  return tipo === 'custo' ? 'Ajuda de Custo' : 'Diárias';
}

/**
 * O valor de um salário no campo.
 *
 * @param salario - O salário gravado.
 * @returns O salário em `pt-BR` com duas casas; vazio quando nulo ou zero.
 */
function salarioNoCampo(salario: number | null): string {
  return salario === null || salario === 0
    ? ''
    : salario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * O cartão de folha.
 *
 * @param favorecido - O favorecido de conta apartada.
 * @param situacao - A situação do mês, com os dias efetivos e a guia.
 * @param fechada - A folha fechada do mês, que troca "Fechar Folha" por "✓
 *   Folha Fechada".
 * @param competencia - O mês aberto.
 * @param podeEditar - Falso para quem só tem `ver`.
 * @param onNovoColaborador - O "+ Novo colaborador" do cabeçalho.
 * @param onImportarSalario - Abre a seleção do PDF do salário.
 * @param onImportarGuia - Abre a seleção do PDF da guia DAE.
 * @returns O cartão.
 */
export function CartaoDeFolha({
  favorecido,
  situacao,
  fechada,
  competencia,
  podeEditar,
  onNovoColaborador,
  onImportarSalario,
  onImportarGuia,
}: CartaoDeFolhaProps) {
  const notificar = useNotificacao();
  const gravarSalario = useGravarSalario();
  const gravarDias = useGravarDias();
  const removerGuia = useRemoverGuiaDeOutrosBancos();
  const fecharFolha = useFecharFolha();
  const mes = { favorecidoId: favorecido.id, competencia };
  const gravando = useCartaoGravando(mes);

  const itens = itensDeOutrosBancos(favorecido.ajudaCusto, situacao.diasEfetivos);
  const comDiaria = itens.filter((item) => item.dias !== null);
  const fixos = itens.filter((item) => item.dias === null);
  const guia = situacao.guiaDae;
  const total = totalDoCartao(situacao.salarioLiquido, itens, guia?.total ?? 0);

  const salarioInicial = salarioNoCampo(situacao.salarioLiquido);
  const ligarSalario = useAoConfirmar((campo) => {
    if (campo.value === salarioInicial) return;
    const valor = lerValorDigitado(campo.value);
    gravarSalario
      .mutateAsync({
        mes,
        corpo: { salarioLiquido: valor > 0 ? valor : null, importado: false },
      })
      .catch((erro: unknown) => {
        // Os dados voltam iguais e a `key` não muda: o campo não recria sozinho.
        campo.value = salarioInicial;
        notificar(mensagemDe(erro), false);
      });
  });

  // Os dois dias gravam juntos, como estão à vista; o tipo sem linha no cartão
  // vai com o efetivo.
  const ligarDias = useAoConfirmar((_campo, bloco) => {
    const diasAVista = (tipo: 'custo' | 'diarias') => {
      const campo = bloco.querySelector<HTMLInputElement>(`input[name="${tipo}"]`);
      return campo === null ? situacao.diasEfetivos[tipo] : diasDoCampo(campo.value);
    };
    gravarDias
      .mutateAsync({
        mes,
        corpo: { custo: diasAVista('custo'), diarias: diasAVista('diarias') },
      })
      .catch((erro: unknown) => {
        for (const campo of bloco.querySelectorAll<HTMLInputElement>('input')) {
          campo.value = campo.defaultValue;
        }
        notificar(mensagemDe(erro), false);
      });
  });

  /** O 🧾 da linha de dias: o recibo só da ajuda de custo (l. 3940–3961). */
  function handleRecibo() {
    if (!itens.some((item) => item.valor > 0)) {
      notificar('Nenhuma ajuda de custo com valor pra este favorecido neste mês.', false);
      return;
    }
    if (!abrirEmAbaNova(caminhoDoReciboDeOutrosBancos(mes))) {
      notificar(
        'O navegador bloqueou a nova janela. Permita pop-ups para gerar o recibo.',
        false,
      );
    }
  }

  /** "Gerar Ordem de Pagamento" (l. 3834–3932): só abre a página, não grava. */
  function handleGerarOrdem() {
    if (itensDaOrdemDePagamento(situacao.salarioLiquido, itens).length === 0) {
      notificar(
        'Informe o salário líquido do mês (ou habilite a ajuda de custo no cadastro) antes de gerar a ordem.',
        false,
      );
      return;
    }
    if (!abrirEmAbaNova(caminhoDaOrdemDePagamento(mes))) {
      notificar(
        'O navegador bloqueou a nova janela. Permita pop-ups para gerar a ordem.',
        false,
      );
    }
  }

  /** "Fechar Folha" (l. 3774–3832). */
  async function handleFecharFolha() {
    const falta = faltaAoRefechar(fechada, (situacao.salarioLiquido ?? 0) > 0, guia !== null);
    if (
      falta !== null &&
      !window.confirm(
        `A folha já fechada deste mês tem ${falta}, mas o cartão está vazio agora (foi zerado após o fechamento). Se continuar, ${falta} será removido do Fechamento. Reimporte antes de fechar de novo, ou confirme para continuar mesmo assim.`,
      )
    ) {
      return;
    }
    if (montarFolhaFechada(situacao.salarioLiquido, itens, guia).itens.length === 0) {
      notificar(
        'Informe o salário líquido (ou habilite a ajuda de custo no cadastro) antes de fechar a folha.',
        false,
      );
      return;
    }
    try {
      await fecharFolha.mutateAsync(mes);
      notificar(
        '✓ Folha fechada — já aparece no Fechamento (cartão de importação zerado para o próximo lançamento)',
      );
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  /** "remover guia": sem pergunta, como na origem (l. 4651–4657). */
  async function handleRemoverGuia() {
    try {
      await removerGuia.mutateAsync(mes);
      notificar('Guia DAE removida deste mês');
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <Cartao className="flex h-full flex-col">
      <CabecalhoDoCartao>
        <TituloDoCartao>{tituloDaFolha(favorecido.contaApartadaDebitoDe)}</TituloDoCartao>
        {podeEditar && (
          <Botao variante="fantasma" tamanho="sm" onClick={onNovoColaborador}>
            + Novo colaborador
          </Botao>
        )}
      </CabecalhoDoCartao>
      <CorpoDoCartao className="flex flex-1 flex-col px-4 py-3">
        <div className="mb-2.5 text-[13px] font-semibold text-foreground">
          {favorecido.nome}
        </div>

        <div className="mb-1 flex items-center justify-between">
          <span
            className={CLASSE_DO_ROTULO}
            title={
              situacao.salarioImportadoEm === null
                ? undefined
                : dicaDoSalario(competencia, situacao.salarioImportadoEm)
            }
          >
            Salário líquido do mês{situacao.salarioImportadoEm === null ? '' : ' ✓'}
          </span>
          {podeEditar && (
            <Botao
              variante="fantasma"
              tamanho="sm"
              className={CLASSE_DO_IMPORTAR}
              onClick={onImportarSalario}
            >
              Importar (PDF)
            </Botao>
          )}
        </div>
        <div className="flex justify-end pt-1 pb-2">
          <input
            key={salarioInicial}
            ref={ligarSalario}
            type="text"
            inputMode="decimal"
            aria-label="Salário líquido do mês"
            placeholder="0,00"
            defaultValue={salarioInicial}
            readOnly={!podeEditar}
            onChange={(evento) => {
              evento.currentTarget.value = mascararCentavos(evento.currentTarget.value);
            }}
            className={cn(CLASSE_DO_CAMPO_SOLTO, 'w-24 text-right')}
          />
        </div>

        {itens.length > 0 ? (
          <div ref={ligarDias} className="border-t border-emb-borda pt-2">
            <div className={cn(CLASSE_DO_ROTULO, 'mb-1')}>Ajuda de custo</div>
            {comDiaria.map((item) => (
              <div key={item.tipo} className="flex items-center gap-2 py-1 text-[12px]">
                <span className="flex-1 text-foreground">
                  {rotuloDosDias(item.tipo)}{' '}
                  <span className="text-texto-fraco">
                    ({formatarReais(item.valorDiaria ?? 0)}/dia)
                  </span>
                </span>
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  icone
                  title="Emitir recibo da ajuda de custo"
                  aria-label="Emitir recibo da ajuda de custo"
                  onClick={handleRecibo}
                  disabled={gravando}
                >
                  <IconReceipt size={15} aria-hidden="true" />
                </Botao>
                <label className="flex items-center gap-1 text-[11px] text-texto-fraco">
                  Dias
                  <input
                    key={item.dias}
                    aria-label={`Dias — ${rotuloDosDias(item.tipo)}`}
                    name={item.tipo}
                    type="number"
                    min={0}
                    max={31}
                    defaultValue={item.dias ?? 0}
                    readOnly={!podeEditar}
                    className={cn(CLASSE_DO_CAMPO_SOLTO, 'w-[52px] text-center')}
                  />
                </label>
                <span className="w-24 text-right tabular-nums">
                  {formatarReais(item.valor)}
                </span>
              </div>
            ))}
            {fixos.map((item) => (
              <div key={item.tipo} className="flex justify-between py-1 text-[12px]">
                <span className="text-foreground">{item.descricao}</span>
                <span className="tabular-nums">{formatarReais(item.valor)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-texto-fraco">
            Nenhuma ajuda de custo habilitada no cadastro deste favorecido.
          </div>
        )}

        <div className="mt-2 border-t border-emb-borda pt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className={CLASSE_DO_ROTULO}>
              Guia DAE
              {guia?.competencia == null ? '' : ` (${competenciaComBarra(guia.competencia)})`}
            </span>
            {podeEditar && (
              <Botao
                variante="fantasma"
                tamanho="sm"
                className={CLASSE_DO_IMPORTAR}
                onClick={onImportarGuia}
              >
                {guia === null ? 'Importar Guia DAE' : 'Reimportar'}
              </Botao>
            )}
          </div>
          {guia === null ? (
            <div className="text-[11px] text-texto-fraco">
              Nenhuma guia importada neste mês.
            </div>
          ) : (
            <>
              {guia.itens.map((item) => (
                <div key={item.posicao} className="flex justify-between py-[3px] text-[12px]">
                  <span className="text-foreground">
                    {item.denominacao || item.subcategoria}
                  </span>
                  <span className="tabular-nums">{formatarReais(item.valor)}</span>
                </div>
              ))}
              {podeEditar && (
                <div className="mt-0.5 text-right">
                  <button
                    type="button"
                    onClick={() => void handleRemoverGuia()}
                    className="cursor-pointer border-0 bg-transparent p-0 text-[11px] text-texto-fraco underline"
                  >
                    remover guia
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-2.5 border-t border-emb-borda pt-2.5">
          <div className="flex flex-wrap gap-2">
            <Botao
              variante="fantasma"
              tamanho="sm"
              onClick={handleGerarOrdem}
              disabled={gravando}
            >
              Gerar Ordem de Pagamento
            </Botao>
            {podeEditar && (
              <Botao
                variante="fantasma"
                tamanho="sm"
                title={
                  fechada === undefined
                    ? 'Envia esses valores para o Fechamento e zera o salário/guia DAE deste cartão'
                    : `Fechada em ${formatarData(fechada.fechadoEm)} — clique para atualizar`
                }
                onClick={() => void handleFecharFolha()}
                disabled={gravando}
              >
                {fechada === undefined ? 'Fechar Folha' : '✓ Folha Fechada'}
              </Botao>
            )}
          </div>
          <span className="text-[13px] font-bold text-foreground">
            Total: {formatarReais(total)}
          </span>
        </div>
      </CorpoDoCartao>
    </Cartao>
  );
}
